// Third-party
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import moment from 'moment';
import { deepClone } from '@/utils/objects';

import { setDefaultAllocationMode, PortfolioData, getPortfolioRiskRating, getPortfolioRiskAssetType, getPortfolioType } from '@/utils/portfolios';
import {
  trimRollingData,
  getCommonTimestamps,
  calculateCalendarYearFromRolling,
  calculatePerformanceCommonInception,
  calculatePortfolioSinceInception,
  initPercentGrowth,
} from '@/utils/calculation-utils';
import { calculateGrowth, getRollingData } from '@/utils/portfolio/utils';
import { COMPARISON_ITEM_TYPES, PortfolioFlag } from '@/utils/constants';
import { addShelvesToPortfolioHoldings, convertPortfolioHoldingsShelvesToShelfStatuses } from '@/utils/shelf-management';
import disclaimers from '@/utils/disclaimers';

import { getAssetAllocations } from './shared';
import Portfolio from '@/types/portfolio';
import AppRegion from '@/types/app-region';
import ComparisonItem from '@/types/comparison-item';
import ComparisonHolding from '@/types/comparison-holding';
import PortfolioOverrideDates from '@/types/portfolio-override-dates';
import ComparisonItemDates from '@/types/comparison-item-dates';
import PortfolioRisk from '@/types/portfolio-risk';
import Comparison from '@/types/comparison';
import { convertHoldingsToComparisonHoldings } from './utils/convert-holdings-to-comparison-holdings';
import { getPortfolioBenchmarks } from './utils/portfolio/get-portfolio-benchmarks';
import { getPortfolioDates } from './utils/portfolio/get-portfolio-dates';
import { calculateAltMetrics } from '@/utils/alts/calculation';
import Shelf from '@/types/shelf';

/**
 * Generates a comparison item from a single portfolio.
 *
 * @param {Portfolio} portfolio Portfolio model object.
 * @return {ComparisonItem} Comparison item based on the portfolio.
 */
const portfolioToComparisonItem = (
  portfolio: Portfolio,
  appRegion: AppRegion,
  validateIncomeStream: boolean,
  portfolioData?: PortfolioData,
  options?: {
    taxRate?: number;
    shortHistory?: boolean;
    itemFlags?: string[];
    comparison?: Comparison;
    existingItem?: ComparisonItem;
  },
): ComparisonItem => {
  const newPortfolio = setDefaultAllocationMode(portfolio);

  let flags = options?.itemFlags || portfolio.flags;
  if (flags && portfolio.flags?.includes(PortfolioFlag.UnallocatedPortfolio)) {
    flags = uniq([...flags, PortfolioFlag.UnallocatedPortfolio]);
  }
  const discretionaryMer = get(newPortfolio, 'discretionaryMer');
  const taxRate = get(options, 'taxRate');
  const tax = discretionaryMer != null && taxRate != null ? taxRate * discretionaryMer : undefined;
  const fees = (discretionaryMer || 0) + (tax || 0);

  let overrideHoldings = [] as ComparisonHolding[];

  const holdings = convertHoldingsToComparisonHoldings(newPortfolio.holdings, portfolioData, options?.existingItem?.holdings);
  const rollingData = getRollingData(portfolio, holdings, fees, options?.shortHistory);

  // The forced rolling data contains back-filled benchmark performance that we want to filter out
  // if a forced inception date is passed in. This is insanely specific to Pacific and only makes sense
  // with this context. Definitely something that should be configured in or reworked. But hey,
  // it was Christmas

  let forcedRollingsForCalculations = rollingData.override ? rollingData.rolling.monthly : null;
  const portfolioOverrideDates = portfolio.override?.dates != null ? (portfolio.override.dates as PortfolioOverrideDates) : null;
  if (forcedRollingsForCalculations && portfolioOverrideDates?.inceptionDate != null) {
    forcedRollingsForCalculations = forcedRollingsForCalculations.filter(
      rolling => rolling[0] > moment.utc(portfolioOverrideDates.inceptionDate).toDate().getTime(),
    );
  }
  if (rollingData.override) {
    const symbols: string[] = newPortfolio.holdings.reduce((acc, h) => {
      if (h.symbol != null) {
        acc.push(h.symbol);
      }
      return acc;
    }, [] as string[]);

    // This was introduced to force performance on a portfolio.
    // Since the app constantly goes for the holdings,
    // we added this holdings override so the forced data is always used
    overrideHoldings = [
      {
        id: portfolio._id,
        type: 'fund',
        name: portfolio.name,
        weight: 100,
        currency: portfolio.currency,
        symbol: symbols.join(', '),
        rolling: getRollingData(portfolio, holdings, 0, options?.shortHistory).rolling, // do not apply the fees!
        dates: portfolio.dates,
        metrics: {},
      } as ComparisonHolding,
    ] as ComparisonHolding[];
  }

  if (holdings.length) {
    const rollings = holdings.map((h: ComparisonHolding) => (h.rolling ? h.rolling : { monthly: [], weekly: [] }));

    if (rollings.length) {
      // Trim the ends before the latest common date
      const trimStamps = getCommonTimestamps(rollings, 'DESC');

      holdings.forEach(item => {
        item.rolling = {
          monthly: trimRollingData(item.rolling && item.rolling.monthly, 0, trimStamps.monthly),
          weekly: trimRollingData(item.rolling && item.rolling.weekly, 0, trimStamps.weekly),
        };

        item.baseRolling = {
          monthly: trimRollingData(item.baseRolling && item.baseRolling.monthly, 0, trimStamps.monthly),
          weekly: trimRollingData(item.baseRolling && item.baseRolling.weekly, 0, trimStamps.weekly),
        };
      });
    }
  }

  const dates: ComparisonItemDates = getPortfolioDates(newPortfolio);
  if (portfolio.override != null && portfolio.override.dates != null && !portfolio.isPartiallyOverriden) {
    dates.portfolioGrowth = { portfolio: (portfolio.override.dates as any).portfolioGrowth };
    dates.growth = (portfolio.override.dates as any).portfolioGrowth;
    dates.inception = new Date((portfolio.override.dates as any).inceptionDate);
  }

  // Derive shelves from portfolio holdings and holdings data
  const derivedHoldings = addShelvesToPortfolioHoldings(newPortfolio.holdings, portfolioData);
  const holdingsShelves = derivedHoldings.map(holding => holding.filteredShelves || []);
  const holdingsComplianceComparisons = derivedHoldings.map(holding => holding.complianceComparisons || []);

  let comparisonItemShelfStatusFlags: string[] = [];
  if (newPortfolio.holdings.length > 0) {
    comparisonItemShelfStatusFlags = [
      ...comparisonItemShelfStatusFlags,
      ...convertPortfolioHoldingsShelvesToShelfStatuses(holdingsShelves as Shelf[][], holdingsComplianceComparisons),
    ];
  }

  const rolling = rollingData.override ? rollingData.rolling : calculateGrowth(holdings, fees, undefined, options?.shortHistory);

  const portfolioRisk = {
    standardDeviation: get(newPortfolio, 'performance.risk.standardDeviation'),
    sharpeRatio: get(newPortfolio, 'performance.risk.sharpeRatio'),
    sortino: get(newPortfolio, 'performance.risk.sortino'),
    beta: get(newPortfolio, 'performance.risk.beta'),
    rSquared: get(newPortfolio, 'performance.risk.rSquared'),
    correlation: get(newPortfolio, 'performance.risk.correlation'),
    maximumDrawdown: get(newPortfolio, 'performance.risk.maximumDrawdown'),
    upside: get(newPortfolio, 'performance.risk.upside'),
    downside: get(newPortfolio, 'performance.risk.downside'),
    alpha: get(newPortfolio, 'performance.risk.alpha'),
    infoRatio: get(newPortfolio, 'performance.risk.infoRatio'),
    trackingError: get(newPortfolio, 'performance.risk.trackingError'),
    correlationCoefficient: get(newPortfolio, 'performance.risk.correlationCoefficient'),
  } as Partial<PortfolioRisk>;

  const portfolioPercentGrowth = newPortfolio?.performance?.percentGrowth || initPercentGrowth();
  const performance = {
    percentGrowth: {
      ...portfolioPercentGrowth,
      INC: portfolioData != null ? calculatePortfolioSinceInception(rolling.monthly, holdings, options?.shortHistory) : null,
      CI: portfolioData != null ? calculatePerformanceCommonInception(rolling.monthly, options?.comparison?.commonInception?.startTimestamp) : null,
    },
    calendarYear:
      forcedRollingsForCalculations != null ? calculateCalendarYearFromRolling(forcedRollingsForCalculations) : get(newPortfolio, 'performance.calendarYear'),
    rolling,
    bestWorst: newPortfolio.performance && newPortfolio.performance.bestWorst,
    upDownMonths: newPortfolio.performance && newPortfolio.performance.upDownMonths,
    ...portfolioRisk,
  };

  const holdingsClone = deepClone(holdings).map(h => ({ ...h, rolling: h.baseRolling }));
  const rollingBase = calculateGrowth(holdingsClone, fees, undefined, options?.shortHistory);

  const portfolioBasePercentGrowth = newPortfolio?.performanceBase?.percentGrowth || initPercentGrowth();
  const performanceBase = {
    percentGrowth: {
      ...portfolioBasePercentGrowth,
      INC: portfolioData != null ? calculatePortfolioSinceInception(rollingBase.monthly, holdings, options?.shortHistory) : null,
      CI: portfolioData != null ? calculatePerformanceCommonInception(rollingBase.monthly, options?.comparison?.commonInception?.startTimestamp) : null,
    },
    calendarYear:
      forcedRollingsForCalculations != null
        ? calculateCalendarYearFromRolling(forcedRollingsForCalculations)
        : get(newPortfolio, 'performanceBase.calendarYear'),
    rolling: rollingBase,
    bestWorst: newPortfolio.performance && newPortfolio.performance.bestWorst,
    upDownMonths: newPortfolio.performance && newPortfolio.performance.upDownMonths,
    ...portfolioRisk,
  };

  const {
    _id,
    allocations,
    availableActions,
    customData,
    contact,
    codes,
    currency,
    clientExtraData,
    detailedHoldings,
    enterpriseGroups,
    enterprise,
    esg,
    metadata,
    mer,
    name,
    investmentInfo,
    planning,
    riskProfile,
    size,
    mine,
    missingHoldingsProxyLog,
  } = newPortfolio;

  const altMetrics = portfolioData?.alts?.length ? calculateAltMetrics(portfolio, portfolioData.alts) : null;

  return {
    _id: _id || '',
    allocations: allocations && {
      asset: allocations.asset && getAssetAllocations(allocations.asset, appRegion),
      country: allocations?.country,
      creditQuality: allocations?.creditQuality,
      detailedAsset: allocations.asset && allocations.asset,
      equityCountry: allocations?.equityCountry,
      equityRegion: allocations?.equityRegion,
      fixedIncomeCountry: allocations?.fixedIncomeCountry,
      fixedIncomeRegion: allocations?.fixedIncomeRegion,
      fixedIncomeSector: allocations?.fixedIncomeSector,
      maturity: allocations?.maturity,
      region: allocations?.region,
      sector: allocations?.sector,
    },
    availableActions,
    benchmarks: {
      market: portfolioData ? getPortfolioBenchmarks(portfolioData).marketBenchmarks : undefined,
      riskFree: portfolioData ? getPortfolioBenchmarks(portfolioData).riskFreeBenchmarks : undefined,
    },
    characteristics: {
      averageCoupon: investmentInfo && investmentInfo.averageCoupon,
      averageQuality: investmentInfo && investmentInfo.averageQuality,
      effectiveMaturity: investmentInfo && investmentInfo.effectiveMaturity,
      returnOnEquity: investmentInfo && investmentInfo.returnOnEquity,
      yieldToMaturity: investmentInfo && investmentInfo.yieldToMaturity,
    },
    clientExtraData,
    codes,
    contact: contact != null ? contact._id : undefined,
    contactsInfo: portfolio.contact ? [portfolio.contact] : undefined,
    currency,
    customData,
    dates,
    detailedHoldings,
    disclaimers: portfolioData
      ? disclaimers.getComparisonPortfolioDisclaimers(
          newPortfolio,
          portfolioData,
          validateIncomeStream,
          options?.shortHistory,
          options?.comparison?.commonInception,
        )
      : {},
    discretionaryMer,
    enterprise,
    enterpriseGroups,
    esg,
    flags: uniq([...(flags || []), ...comparisonItemShelfStatusFlags]),
    holdings,
    investmentInfo: investmentInfo && {
      capitalization: investmentInfo.capitalization,
      creditRatingClass: investmentInfo.creditRatingClass,
      distributionYield: investmentInfo.distributionYield,
      durationClass: investmentInfo.durationClass,
      effectiveDuration: investmentInfo && investmentInfo.effectiveDuration,
      modifiedDuration: investmentInfo.modifiedDuration,
      pbRatio: investmentInfo.pbRatio,
      peRatio: investmentInfo.peRatio,
      style: investmentInfo.style,
    },
    mer,
    metadata,
    name,
    overrideHoldings: overrideHoldings.length > 0 ? overrideHoldings : undefined,
    performance,
    performanceBase,
    ...(planning ? { planning } : {}),
    portfolioType: getPortfolioType(newPortfolio),
    riskAssetType: getPortfolioRiskAssetType(newPortfolio, portfolioData),
    riskProfile: riskProfile?.profile,
    riskRating: getPortfolioRiskRating(newPortfolio, portfolioData),
    size,
    source: newPortfolio,
    tax,
    mine,
    type: COMPARISON_ITEM_TYPES.PORTFOLIO,
    category: options?.comparison?.items?.find(item => item._id === newPortfolio._id)?.category,
    missingHoldingsProxyLog,
    ...altMetrics,
  };
};

export default portfolioToComparisonItem;
